import { useState } from 'react';

import { UseMutateAsyncFunction } from '@tanstack/react-query';
import { AxiosResponse } from 'axios';
import styled from 'styled-components';

import { CopyrightFooter } from '@components/copyright-footer/copyright-footer';
import { Form, useFormContext } from '@components/form';
import { isV2Form } from '@components/form-builder';
import { FormError } from '@components/form-error';
import { Field, prepareFormData } from '@components/form/dynamic-fields';
import { DynamicFields, FormAnswers, FormV2 } from '@components/form/v2';
import { prepareFormDataV2 } from '@components/form/v2/utils';
import { Stack } from '@components/stack';
import { useIsMobile } from '@hooks/use-is-mobile';
import { useScopedTranslate } from '@locale/translate';

import { SaveFormResponse } from '../api/request';

type OnSubmit = UseMutateAsyncFunction<
  AxiosResponse<SaveFormResponse, any>,
  unknown,
  FormData,
  unknown
>;

export const DisplayForm = ({
  formConfig,
  onSubmit,
  formAnswers,
}: {
  formConfig: Field[] | FormV2;
  onSubmit: OnSubmit;
  formAnswers?: FormAnswers;
}) => {
  if (isV2Form(formConfig)) {
    return <RenderV2Form formConfig={formConfig} onSubmit={onSubmit} formAnswers={formAnswers} />;
  }

  return <RenderV1Form formConfig={formConfig} onSubmit={onSubmit} />;
};

const RenderV2Form = ({
  formConfig,
  onSubmit,
  formAnswers,
}: {
  onSubmit: OnSubmit;
  formConfig: FormV2;
  formAnswers?: FormAnswers;
}) => {
  const formHasSubmitButton = Boolean(formConfig.fields.find((field) => field.kind === 'submit'));
  const isMobile = useIsMobile();
  return (
    <Container data-mobile={isMobile}>
      <Form
        space={6}
        onSubmit={(values, action) => {
          const prepared = prepareFormDataV2(formConfig.fields, values);
          prepared.set('isComplete', action === 'save' ? 'false' : 'true');
          return onSubmit(prepared);
        }}
      >
        <DynamicFields fields={formConfig.fields} formAnswers={formAnswers} />
        {!formHasSubmitButton && <FormActions />}
        <CopyrightFooter />
      </Form>
    </Container>
  );
};

const RenderV1Form = ({ formConfig, onSubmit }: { onSubmit: OnSubmit; formConfig: Field[] }) => {
  const [form, setForm] = useState(formConfig);

  return (
    <Container>
      <Form
        space={6}
        onSubmit={(values) => {
          const prepared = prepareFormData(form, values, {});
          prepared.set('fieldsConfig', prepared.get('form')!);
          prepared.delete('form');
          return onSubmit(prepared);
        }}
      >
        <Form.DynamicFields
          initialFields={form}
          externalConditionData={{}}
          onFieldsUpdate={setForm}
        />
        <FormActions />
        <CopyrightFooter />
      </Form>
    </Container>
  );
};

const Container = styled.div({
  maxWidth: '35rem',
  margin: 'auto',
  padding: '4rem 2.5rem',
});

export const FormActions = () => {
  const {
    store: { useFormHasErrors, useHasSubmit },
  } = useFormContext();
  const hasErrors = useFormHasErrors();
  const hasSubmit = useHasSubmit();
  const { t } = useScopedTranslate(translations);
  return (
    <div>
      <Form.Actions>
        <Form.Submit variant="primary" text={t('submit')} />
      </Form.Actions>
      <Stack space={0} align="flex-end">
        {hasSubmit && hasErrors && <FormError text={t('contains_errors')} />}
      </Stack>
    </div>
  );
};

const translations = {
  is: {
    submit: 'Senda svör',
    contains_errors: 'Ekki tókst að senda formið þar sem það inniheldur villur',
  },
  en: {
    submit: 'Send answers',
    contains_errors: 'Unable to send the form as it contains errors',
  },
  dk: {
    submit: 'Send svar',
    contains_errors: 'Formularen kunne ikke sendes, da den indeholder fejl',
  },
};
